import React, { useCallback, useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { Button } from '../../ui/Button';
import { opportunityWatchService } from '../../../services/opportunityWatchService';
import { followUpAiService } from '../../../services/followUpAiService';
import { WatchCard } from './WatchCard';

const GROUPS = [
  ['Needs your attention', ['new_info', 'needs_review']],
  ['Active watches', ['watching']],
  ['Paused', ['paused']],
  ['Completed or stopped', ['completed']]
];

export const OpportunityWatchPanel = ({ userId, version, onFlash, onFollowUpSaved }) => {
  const [watches, setWatches] = useState([]);
  const [notes, setNotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [w, n] = await Promise.all([
        opportunityWatchService.list(userId),
        opportunityWatchService.notifications(userId)
      ]);
      setWatches(w);
      setNotes(n);
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load, version]);
  useEffect(() => {
    followUpAiService.listContacts().then(setContacts).catch(() => {});
  }, []);

  const changed = (followUpAdded) => {
    load();
    if (followUpAdded) onFollowUpSaved?.();
  };

  const dismiss = async (id) => {
    try {
      await opportunityWatchService.markRead(id);
      setNotes((prev) => prev.filter((n) => n.id !== id));
    } catch (e) {
      onFlash('error', e.message);
    }
  };

  if (loading) return null;
  if (error) return <p className="mb-6 text-xs text-red-600">Opportunity Watch is unavailable: {error}</p>;
  if (watches.length === 0 && notes.length === 0) return null;

  return (
    <div className="mb-10">
      <h2 className="mb-4 flex items-center text-lg font-bold text-slate-900">
        <SafeIcon icon={FiIcons.FiEye} className="mr-2 text-violet-500" /> Opportunity Watches
        <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{watches.length}</span>
      </h2>

      {notes.length > 0 && (
        <div className="mb-5 space-y-3">
          {notes.map((n) => (
            <div key={n.id} className="rounded-xl border border-green-200 bg-green-50 p-4">
              <p className="flex items-center text-sm font-bold text-green-800">
                <SafeIcon icon={FiIcons.FiBell} className="mr-2" /> {n.title}
              </p>
              <p className="mt-1 text-sm text-slate-700">{n.body}</p>
              {n.source_url && (
                <p className="mt-1 break-all text-xs">
                  <a href={n.source_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">{n.source_url}</a>
                </p>
              )}
              <div className="mt-2 flex flex-wrap gap-2">
                <Button size="sm" onClick={() => { setOpenId(n.watch_id); document.getElementById(`watch-${n.watch_id}`)?.scrollIntoView({ behavior: 'smooth' }); }}>
                  Review & create follow-up
                </Button>
                <Button size="sm" variant="ghost" onClick={() => dismiss(n.id)}>Dismiss</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {GROUPS.map(([title, statuses]) => {
        const items = watches.filter((w) => statuses.includes(w.status));
        if (!items.length) return null;
        return (
          <div key={title} className="mb-5">
            <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-500">{title} ({items.length})</p>
            <div className="space-y-3">
              {items.map((w) => (
                <div key={`${w.id}-${openId === w.id}`} id={`watch-${w.id}`}>
                  <WatchCard
                    userId={userId}
                    watch={w}
                    contacts={contacts}
                    onContactAdded={(c) => setContacts((prev) => [...prev, c])}
                    onFlash={onFlash}
                    onChanged={changed}
                    startOpen={openId === w.id}
                  />
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};